/* eslint-disable max-lines */
import { EventBus, Events } from "jiffy-store";
import { track } from "jiffy-analytics";
import { redirectTo } from "common/utils/browser";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { trackProductAddedModal } from "components/jiffy/product-details-quantity-form/analytics";
import { on } from "common/utils/ui/event";
import { fetchPdpDeliveryEstimates } from "common/utils/delivery-date/fetch-pdp-delivery-estimates";
import { formatDeliveryDateForDisplay } from "common/utils/format/date";
import ModalFileUpload from "./modal-file-upload";
import "./added-to-cart-modal-v2.scss";

const $$getModal = () => $("#added-to-cart-modal-v2");
const $getPromoTitleMobile = () =>
  document.querySelector(".js-added-to-cart-modal-v2-promo-title-mobile");
const $getPromoTitleDesktop = () =>
  document.querySelector(".js-added-to-cart-modal-v2-promo-title-desktop");

let initialized = false;
let cachedDtfDeliveryDate = null;
let uploadHandler = null;
let trackingAdditionalProps = {};

const DTF_PRODUCT_SLUG = "jiffytransfers-DTF001";
const DTF_COMPOSITION_NAME = "Transfer";

/**
 * Fetch delivery date for DTF transfers
 * @returns {Promise<string|null>} The formatted delivery date or null
 */
const fetchDtfDeliveryDate = async () => {
  if (cachedDtfDeliveryDate) {
    return cachedDtfDeliveryDate;
  }

  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const payload = {
      composition_name: DTF_COMPOSITION_NAME,
      variants_hash: JSON.stringify({}),
      pre_cut: false,
      dtf_print: [{ height: 1, width: 1, quantity: 1 }],
      browser_timezone: browserTimezone
    };

    const response = await fetchPdpDeliveryEstimates({ productId: DTF_PRODUCT_SLUG }, payload);

    if (response && response.composition_dates && response.composition_dates.value_full) {
      cachedDtfDeliveryDate = response.composition_dates.value_full;
      return cachedDtfDeliveryDate;
    }

    return null;
  } catch (error) {
    console.warn("Failed to fetch DTF delivery date:", error);
    return null;
  }
};

/**
 * Update the delivery date in the modal
 * @param {string} deliveryDate The formatted delivery date
 */
const updateModalDeliveryDate = deliveryDate => {
  const $deliveryDateSpan = document.querySelector(
    "#added-to-cart-modal-v2 .added-to-cart-modal-v2__delivery-date"
  );
  if (!$deliveryDateSpan || !deliveryDate) return;

  const prev = $deliveryDateSpan.previousSibling;
  const hasComma = prev && prev.nodeType === Node.TEXT_NODE && prev.textContent === ", ";
  if (!hasComma) {
    $deliveryDateSpan.parentNode.insertBefore(document.createTextNode(", "), $deliveryDateSpan);
  }
  $deliveryDateSpan.textContent = formatDeliveryDateForDisplay(deliveryDate);
};

/**
 * Update the modal promo title
 * @param {HTMLElement} $element The element to update
 * @param {string} title The promo title text to update
 */
const updateModalPromoTitle = ($element, title) => {
  if ($element && title) {
    $element.textContent = title;
    $element.classList.remove("hidden");
  }
};

const BOGO_DIMENSION_FALLBACK = "12x12";

/**
 * Build delivery dimension copy from backend label (e.g. "12x12", "any size").
 * @param {string|null|undefined} label From API max_free_dimension_label
 * @returns {string}
 */
const bogoDeliveryDimensionText = label => {
  const value = label || BOGO_DIMENSION_FALLBACK;
  return value === "any size"
    ? "Get any size Transfers delivered free"
    : `Get up to ${value} Transfers delivered free`;
};

/**
 * Update the BOGO dimension copy in the modal (e.g. "Get up to 12x12 Transfers delivered free").
 * When label is null/undefined, the server-rendered text is preserved.
 * @param {string|null|undefined} label From API max_free_dimension_label
 */
const updateModalBogoDimensionText = label => {
  if (!label) return;
  const $el = document.querySelector(".js-added-to-cart-modal-v2-bogo-dimension-text");
  if ($el) {
    $el.textContent = bogoDeliveryDimensionText(label);
  }
};

const getCartContents = () => [
  "Blanks", // ATC modal only shows after adding blanks
  ...(document.pdpDynamicData?.has_transfers_in_cart ? ["Transfers"] : [])
];

/**
 * Sync added-item summary in v2 modal from v1 modal's populated items (size).
 */
const syncAddedItemSummaryFromV1Modal = () => {
  const firstSizeEl = document.querySelector(
    "#continue-shopping-modal .continue-shopping-modal__items .js-continue-shopping-modal-size"
  );
  const addedSize = firstSizeEl?.textContent?.trim();
  const $summaryEl = document.querySelector(".js-added-to-cart-modal-v2-added-item-summary");
  if (!$summaryEl) return;
  if (addedSize) {
    $summaryEl.textContent = `Size: ${addedSize}`;
    $summaryEl.classList.remove("hidden");
  } else {
    $summaryEl.textContent = "";
    $summaryEl.classList.add("hidden");
  }
};

/**
 * Handle chat link click to open Tidio chat widget
 */
const handleChatClick = event => {
  event.preventDefault();
  event.stopPropagation();

  // Disable Bootstrap modal focus enforcement to allow typing in Tidio chat
  $(document).off("focusin.bs.modal");

  const chatWidget = document.getElementById("tidio-chat");
  if (chatWidget) {
    setTimeout(() => {
      window.tidioChatApi?.show?.();
      window.tidioChatApi?.open?.();
    }, 100);
  }
};

// eslint-disable-next-line max-lines-per-function
const initModal = () => {
  const $modal = document.getElementById("added-to-cart-modal-v2");
  if ($modal) {
    uploadHandler = new ModalFileUpload($modal);
  }

  on("click", "#added-to-cart-modal-v2 .js-button-view-cart", () => {
    trackProductAddedModal("view_cart", {
      ...trackingAdditionalProps,
      modal: "free_transfers_atc",
      items_added: undefined,
      items_value: undefined
    });
    $$getModal().modal("hide");
    redirectTo("/cart");
  });

  on("click", "#added-to-cart-modal-v2 .js-button-continue-shopping", () => {
    submitDtfEvent("dtf_continue_shopping_modal_click", {
      action: "continue_shopping",
      business_line: trackingAdditionalProps.business_line || "Transfers",
      free_shipping_threshold: trackingAdditionalProps.free_shipping_threshold,
      catalog_number: trackingAdditionalProps.catalog_number,
      modal: "free_transfers_atc"
    });
  });

  on("click", "#added-to-cart-modal-v2 .js-modal-upload-label", () => {
    submitDtfEvent("transfer_choose_file_clicked", {
      page: "pdp",
      pdp_type: "blanks",
      catalog_number: trackingAdditionalProps.catalog_number || "DTF001",
      modal: "free_transfers_atc"
    });
  });

  on("change", "#added-to-cart-modal-v2 .js-modal-file-input", e => {
    const [file] = e.target.files;
    if (!file) return;

    e.target.value = "";

    if (uploadHandler && uploadHandler.handleFileSelection(file)) {
      uploadHandler.uploadArtwork();
    }
  });

  on("click", "#added-to-cart-modal-v2 .js-modal-retry-button", () => {
    const $fileInput = document.getElementById("modal-file-input");
    if ($fileInput) {
      $fileInput.click();
    }
  });

  on("click", "#added-to-cart-modal-v2 .js-chat-link", handleChatClick);

  on("click", "#added-to-cart-modal-v2 .js-bogo-modal-signup-link", () => {
    track("click_discount_signup_link", {
      current_page: "Cart",
      cart_contents: getCartContents()
    });
  });

  EventBus.on(Events.ITEMS_ADDED_TO_CART, ({ target }) => {
    const { free_shipping_threshold: shippingThreshold } = target || {};
    const $continueShoppingModal = document.getElementById("continue-shopping-modal");
    const businessLine = $continueShoppingModal?.dataset?.businessLine || "Transfers";
    const catalogNumber = $continueShoppingModal?.dataset?.catalogNumber;

    trackingAdditionalProps = {
      free_shipping_threshold: shippingThreshold,
      business_line: businessLine,
      catalog_number: catalogNumber
    };
  });

  EventBus.on(Events.ZIPCODE_UPDATED, () => {
    cachedDtfDeliveryDate = null;
  });

  EventBus.on(Events.SHOW_ADDED_TO_CART_MODAL_V2, async (data = {}) => {
    const $$modal = $$getModal();
    if ($$modal.length > 0) {
      const $$regularModal = $("#continue-shopping-modal");
      syncAddedItemSummaryFromV1Modal();

      if ($$regularModal.length > 0 && $$regularModal.is(":visible")) {
        $$regularModal.modal("hide");
      }

      if (data.trackingProps) {
        trackingAdditionalProps = { ...data.trackingProps };
      }

      if (!trackingAdditionalProps.business_line) {
        trackingAdditionalProps.business_line = "Transfers";
      }

      $$modal.modal("show");

      track("added_to_cart_modal_opened", { modal_type: "Free Transfers" });

      uploadHandler?.resetUI?.();
      // Use BOGO message from backend
      const bogoMessage =
        data.bogoMessage || data.target?.bogoMessage || "Buy blanks, get free transfers!";

      updateModalPromoTitle($getPromoTitleMobile(), bogoMessage);
      updateModalPromoTitle($getPromoTitleDesktop(), bogoMessage);

      const dimensionLabel = data.maxFreeDimensionLabel ?? data.target?.max_free_dimension_label;
      updateModalBogoDimensionText(dimensionLabel);

      const deliveryDate = await fetchDtfDeliveryDate();
      if (deliveryDate) {
        updateModalDeliveryDate(deliveryDate);
      }
    }
  });
};

export default () => {
  if (!initialized) {
    initModal();
    initialized = true;
  }
};
